import type { EFile, Transmission } from '@tabservice/tabfile-client';
import { isInFlight } from '@tabservice/tabfile-client';
import { formatClock, toDate } from '@/lib/format';

/**
 * What an e-file that has not finished is waiting for, and when that wait ends.
 *
 * "Queued" and "Awaiting IRS acknowledgement" are true, and they are the two sentences a
 * customer reads most often on a page they opened precisely because they want to know how
 * long this is going to take. Neither answers that, so the page gets refreshed by hand every
 * few minutes and the wait feels open-ended when it is not.
 *
 * Every number below comes from something Tab Service actually does, not from a guess. The
 * send worker wakes twice a minute and takes four chunks each time; the status poller asks
 * the IRS on a schedule it records on the transmission itself, as `nextPollAt`, so that time
 * is read off the row rather than worked out again here. The one thing this deliberately does
 * not promise is when the IRS will answer: that is the IRS's to decide, and an invented
 * estimate would be the one sentence on the page that could be wrong.
 */

/** The send worker wakes every 30 seconds: apps/api/src/workers/sendDrainer.ts. */
const SEND_TICK_SECONDS = 30;
/** How many chunks that worker takes on each tick. Same file, BATCH_SIZE. */
const CHUNKS_PER_TICK = 4;

export interface WaitingSubject {
  status: EFile['status'];
  transmissions?: Transmission[];
}

/**
 * One sentence for the top of the e-file, or null when there is nothing to wait for.
 *
 * Null for every settled status, so a caller can fall back to the ordinary status hint and
 * an accepted e-file is never told that something is about to happen to it.
 */
export function waitingLine(filing: WaitingSubject, now: Date = new Date()): string | null {
  if (!isInFlight(filing.status)) return null;
  const transmissions = filing.transmissions ?? [];

  if (filing.status === 'queued') {
    return transmissions.length === 0
      ? 'The records are still being written into transmissions. Sending starts within a minute of the last one.'
      : `${chunkCount(transmissions.length)} ready to send. Sending starts within a minute.`;
  }

  if (filing.status === 'transmitting') {
    const sent = transmissions.filter((item) => item.sentAt).length;
    const total = transmissions.length;
    const left = Math.max(0, total - sent);
    if (total === 0) return 'Sending to the IRS now.';
    if (left === 0) return `All ${chunkCount(total)} sent. Waiting for the IRS to acknowledge them.`;
    return `${sent} of ${total} chunks sent. ${remainingSendTime(left)}`;
  }

  // processing: everything is with the IRS and the answer is theirs to give.
  const next = nextCheckAt(transmissions, now);
  const sentAgo = longestWait(transmissions, now);
  const opening = sentAgo === null ? 'The IRS has not answered yet.' : `With the IRS for ${sentAgo}.`;
  return next
    ? `${opening} Tab Service asks again at ${formatClock(next, now)}, and this page updates on its own.`
    : `${opening} Tab Service is asking again now.`;
}

/** "1 chunk" / "6 chunks", because "1 chunks ready to send" reads as a bug. */
function chunkCount(total: number): string {
  return `${total} ${total === 1 ? 'chunk' : 'chunks'}`;
}

/**
 * How long the chunks that have not gone yet should take.
 *
 * Rounded up to whole ticks and then to whole minutes, and phrased as "about": the estimate
 * is the worker's own rhythm, and the IRS round trip inside each tick can stretch it.
 */
function remainingSendTime(left: number): string {
  const ticks = Math.ceil(left / CHUNKS_PER_TICK);
  if (ticks <= 2) return 'The rest go out within a minute.';
  const minutes = Math.ceil((ticks * SEND_TICK_SECONDS) / 60);
  return `The rest take about ${minutes} minutes.`;
}

/** The soonest scheduled check across the chunks still in flight. */
function nextCheckAt(transmissions: Transmission[], now: Date): Date | null {
  const times = transmissions
    .filter((item) => isInFlight(item.status))
    .map((item) => toDate(item.nextPollAt))
    .filter((date): date is Date => date !== null && date.getTime() > now.getTime())
    .sort((a, b) => a.getTime() - b.getTime());
  return times[0] ?? null;
}

/**
 * How long the oldest unanswered chunk has been out.
 *
 * The oldest rather than the newest: an e-file is only as answered as its slowest chunk, and
 * "4 minutes" beside a chunk that has been out for two hours is the wrong half of the story.
 */
function longestWait(transmissions: Transmission[], now: Date): string | null {
  const sent = transmissions
    .filter((item) => isInFlight(item.status))
    .map((item) => toDate(item.sentAt))
    .filter((date): date is Date => date !== null)
    .sort((a, b) => a.getTime() - b.getTime());
  const oldest = sent[0];
  if (!oldest) return null;
  const minutes = Math.floor((now.getTime() - oldest.getTime()) / 60_000);
  if (minutes < 1) return 'under a minute';
  if (minutes < 60) return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
  const hours = Math.floor(minutes / 60);
  return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
}
